import { Stack } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import PolicyApi from '../../../api/PolicyApi';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { isEmpty } from '../../../util/util';
import DropdownButton from '../../DropdownButton';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import { errorToast, successToast } from '../../Toast';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
	errorData: ErrorData;
}

const DeclinedQuoteActions = (props: Props) => {
	const navigate = useNavigate();
	const methods = useFormContext();
	const reset = methods?.reset;
	const { authData, policyData, errorData } = props;
	const { hasPermission } = authData;
	const {
		setLoading,
		policy,
		quotes,
		selectedQuote,
		quoteUpdateObj,
		setQuoteUpdateObj,
	} = props.policyData;

	const discardQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.discardQuote(authorizationToken, selectedQuote.locator);
			successToast('Successfully discarded quote');
			const newSelectedQuote = quotes.filter(
				(quote) => quote.locator !== selectedQuote.locator,
			)[0];
			await PolicyApi.selectQuote(authorizationToken, newSelectedQuote.locator);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			navigate(`/policy/${policy.locator}/quotes/${newSelectedQuote.locator}`);
			await policyData.loadData(policy.locator);
			setLoading(false);
			return;
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to discard quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const createNewQuote = async () => {
		// if quote name is empty, throw error
		if (!isEmpty(quoteUpdateObj) && quoteUpdateObj.name === '') {
			errorToast('Please enter quote name before creating a new quote');
			return;
		}
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			const newQuote = await PolicyApi.createNewQuote(
				authorizationToken,
				selectedQuote.locator,
			);
			setQuoteUpdateObj({}); // reset update obj
			await PolicyApi.selectQuote(authorizationToken, newQuote.data.locator);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			successToast('Successfully created new quote');
			navigate(`/policy/${policy.locator}/quotes/${newQuote.data.locator}`);
			await policyData.loadData(policy.locator);
			setLoading(false);
			return;
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to create new quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				setLoading(false);
				return;
			}
		}
	};

	const discardPolicy = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.discardPolicy(authorizationToken, policy.locator);
			successToast('Successfully discarded policy');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to discard policy');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		policyData.clear();
		navigate(`/policyholder/${policyData.policy.policyholderLocator}`);
	};

	const hiddenOptions = [];

	hasPermission('quote.create') &&
		hasPermission('quote.quote') &&
		hiddenOptions.push({ label: 'Create New Quote', callback: createNewQuote });

	if (quotes.length > 1) {
		hasPermission('quote.discard') &&
			hasPermission('quote.quote') &&
			hiddenOptions.push({ label: 'Discard Quote', callback: discardQuote });
	} else {
		hasPermission('policy.discard') &&
			hiddenOptions.push({ label: 'Discard Policy', callback: discardPolicy });
	}

	return (
		<Stack direction='row' spacing={1}>
			<DropdownButton actions={hiddenOptions} />
		</Stack>
	);
};

export default withPolicyContext<Props>(DeclinedQuoteActions);
